/**
 * Account-activation interstitial. Reached by opening the WhatsApp invite link
 * (`https://app.gately.ma/invite?token=…`) in a browser — i.e. the app is NOT
 * installed, or the universal / App Link failed to associate. Sign-in is
 * mobile-only, so this page never verifies the token; it routes the résident to
 * the store to install the app, then re-open the same link.
 *
 * Lives at the top level (outside `_dashboard`) so it renders signed-out with
 * no console shell — same as the public legal pages.
 */
import { useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";

import { Button } from "@gately/ui/button";
import { Logo } from "@gately/ui/logo";

import {
  APP_STORE_URL,
  detectPlatform,
  pickStoreTarget,
  PLAY_STORE_URL,
} from "~/lib/store-target";

export const Route = createFileRoute("/invite")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  component: InvitePage,
});

function InvitePage() {
  const { token } = Route.useSearch();
  const platform =
    typeof navigator !== "undefined"
      ? detectPlatform(navigator.userAgent)
      : "other";
  const target = pickStoreTarget(platform);

  // Best-effort: if the app IS installed but the universal / App Link failed to
  // associate, the custom scheme still opens it on /invite where the token gets
  // verified. Harmless no-op when the app isn't installed.
  useEffect(() => {
    if (token && platform !== "other") {
      window.location.href = `gately://invite?token=${encodeURIComponent(token)}`;
    }
  }, [token, platform]);

  return (
    <main className="bg-background text-foreground flex min-h-screen flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm text-center">
        <Link to="/" aria-label="Gately" className="inline-flex justify-center">
          <Logo variant="dark" withWordmark className="dark:hidden" />
          <Logo
            variant="light"
            withWordmark
            className="hidden dark:inline-flex"
          />
        </Link>

        <p className="text-primary mt-10 font-mono text-xs tracking-[0.14em] uppercase">
          Invitation
        </p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight">
          Activez votre compte
        </h1>
        <p className="text-muted-foreground mt-3 text-base leading-relaxed">
          Installez l'application Gately, puis rouvrez ce lien pour accéder à
          votre espace Gately.
        </p>

        <div className="mt-8 flex flex-col gap-3">
          {target.url ? (
            <Button asChild size="lg">
              <a href={target.url}>Télécharger sur {target.label}</a>
            </Button>
          ) : (
            <>
              <Button asChild size="lg">
                <a href={APP_STORE_URL}>App Store</a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href={PLAY_STORE_URL}>Google Play</a>
              </Button>
            </>
          )}
        </div>

        <p className="text-muted-foreground mt-8 text-sm leading-relaxed">
          Vous avez déjà l'application ? Ouvrez-la et connectez-vous avec votre
          numéro de téléphone.
        </p>
      </div>
    </main>
  );
}
